import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Edit2, Trash2, HelpCircle, ChevronDown,
  Tag, Languages, RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from '../../components/ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { useAuth, API } from '../../App';
import { toast } from 'sonner';
import axios from 'axios';

import ChannelSelector from '../../components/ChannelSelector';

const FAQPage = () => {
  const { token, user } = useAuth();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  
  // Form state
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [category, setCategory] = useState('');
  const [language, setLanguage] = useState('en');
  const [priority, setPriority] = useState(0);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (selectedChannelId) {
      setLoading(true);
      fetchFaqs();
    }
  }, [categoryFilter, selectedChannelId]);

  const fetchFaqs = async () => {
    try {
      const params = categoryFilter !== 'all' ? { category: categoryFilter } : {};
      let url;
      if (selectedChannelId && selectedChannelId !== 'all') {
        url = `${API}/channels/${selectedChannelId}/faqs`;
      } else {
        url = `${API}/faqs`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setFaqs(response.data);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setQuestion('');
    setAnswer('');
    setCategory('');
    setLanguage('en');
    setPriority(0);
    setIsActive(true);
    setEditingFaq(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (faq) => {
    setEditingFaq(faq);
    setQuestion(faq.question);
    setAnswer(faq.answer);
    setCategory(faq.category || '');
    setLanguage(faq.language);
    setPriority(faq.priority);
    setIsActive(faq.is_active);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      question,
      answer,
      category: category || null,
      language,
      priority,
      is_active: isActive
    };
    
    try {
      if (editingFaq) {
        const url = selectedChannelId && selectedChannelId !== 'all'
          ? `${API}/channels/${selectedChannelId}/faqs/${editingFaq.id}`
          : `${API}/faqs/${editingFaq.id}`;
        await axios.put(url, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('FAQ updated');
      } else {
        const url = selectedChannelId && selectedChannelId !== 'all'
          ? `${API}/channels/${selectedChannelId}/faqs`
          : `${API}/faqs`;
        await axios.post(url, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('FAQ created');
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchFaqs();
    } catch (error) {
      toast.error('Failed to save FAQ');
    }
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    
    try {
      const url = selectedChannelId && selectedChannelId !== 'all'
        ? `${API}/channels/${selectedChannelId}/faqs/${faqId}`
        : `${API}/faqs/${faqId}`;
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('FAQ deleted');
      fetchFaqs();
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };

  const toggleActive = async (faq) => {
    try {
      await axios.put(`${API}/faqs/${faq.id}`, { is_active: !faq.is_active }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchFaqs();
      toast.success(`FAQ ${!faq.is_active ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to update FAQ');
    }
  };

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const categories = [...new Set(faqs.map(f => f.category).filter(Boolean))];

  return (
    <div className="space-y-6" data-testid="faq-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">FAQ & Knowledge Base</h1>
          <p className="text-muted-foreground">Manage your FAQ entries for AI-assisted responses</p>
          <div className="mt-2" data-testid="faq-channel-selector">
            <ChannelSelector
              value={selectedChannelId}
              onChange={(channelId) => setSelectedChannelId(channelId)}
              showAllOption={user?.role === 'owner' || user?.is_super_admin}
            />
          </div>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog} className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-faq-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add FAQ
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingFaq ? 'Edit FAQ' : 'Create New FAQ'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="question">Question</Label>
                <Input
                  id="question"
                  placeholder="What question does this FAQ answer?"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  required
                  data-testid="faq-question-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="answer">Answer</Label>
                <Textarea
                  id="answer"
                  placeholder="Provide a detailed answer..."
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  required
                  rows={4}
                  data-testid="faq-answer-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Input
                    id="category"
                    placeholder="e.g., Shipping, Returns"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    data-testid="faq-category-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="language">Language</Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger data-testid="faq-language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="es">Spanish</SelectItem>
                      <SelectItem value="fr">French</SelectItem>
                      <SelectItem value="de">German</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="priority">Priority (0-10)</Label>
                  <Input
                    id="priority"
                    type="number"
                    min="0"
                    max="10"
                    value={priority}
                    onChange={(e) => setPriority(parseInt(e.target.value) || 0)}
                    data-testid="faq-priority-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Active Status</Label>
                  <div className="flex items-center gap-2 h-10">
                    <Switch
                      checked={isActive}
                      onCheckedChange={setIsActive}
                      data-testid="faq-active-switch"
                    />
                    <span className="text-sm text-muted-foreground">
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-faq-btn">
                  {editingFaq ? 'Update FAQ' : 'Create FAQ'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search FAQs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="faq-search"
          />
        </div>
        
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48" data-testid="category-filter">
            <Tag className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* FAQ List */}
      <div className="space-y-4">
        {filteredFaqs.length === 0 ? (
          <div className="text-center py-12 bg-card rounded-xl border">
            <HelpCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No FAQs found</p>
            <p className="text-sm text-muted-foreground mt-1">Create your first FAQ to help the AI respond better</p>
          </div>
        ) : (
          filteredFaqs.map(faq => (
            <div 
              key={faq.id} 
              className={`p-6 bg-card rounded-xl border ${!faq.is_active ? 'opacity-60' : ''}`}
              data-testid={`faq-item-${faq.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <HelpCircle className="w-5 h-5 text-emerald-600" />
                    <h3 className="font-semibold">{faq.question}</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">{faq.answer}</p>
                  <div className="flex flex-wrap gap-2">
                    {faq.category && (
                      <Badge variant="outline">
                        <Tag className="w-3 h-3 mr-1" />
                        {faq.category}
                      </Badge>
                    )}
                    <Badge variant="outline">
                      <Languages className="w-3 h-3 mr-1" />
                      {faq.language.toUpperCase()}
                    </Badge>
                    <Badge variant={faq.is_active ? 'default' : 'secondary'} className={faq.is_active ? 'bg-emerald-600' : ''}>
                      {faq.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    {faq.priority > 0 && (
                      <Badge variant="outline">Priority: {faq.priority}</Badge>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => openEditDialog(faq)}
                    data-testid={`edit-faq-${faq.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => handleDelete(faq.id)}
                    className="text-red-500 hover:text-red-600"
                    data-testid={`delete-faq-${faq.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FAQPage;
